/**
 * In a text box Ctrl+Z is the text's own undo (last-change.ts). That holds while the box has typing
 * of its own to undo. A box the person only tabbed or clicked into, and has typed nothing in, has
 * none: there Ctrl+Z takes back the value they just saved in the field they left (owner,
 * 2026-10-07: "they type something, maybe they tab to the next field ... if they hit Ctrl+Z, does
 * it undo?"). A box counts as untouched while its text is what it held when it took the keyboard.
 */
const heldAtFocus = new WeakMap<Element, string>();
/** Boxes whose own typing Ctrl+Z took back since they took the keyboard: Ctrl+Y can put it back. */
const undoneSinceFocus = new WeakSet<Element>();

const textBox = (target: EventTarget | null) =>
  target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target : null;

if (typeof window !== 'undefined')
  window.addEventListener(
    'focusin',
    (event) => {
      const box = textBox(event.target);
      if (box) {
        heldAtFocus.set(box, box.value);
        undoneSinceFocus.delete(box);
      }
    },
    true,
  );

/** A window that holds the keyboard. A menu is not one: under it the keyboard stays where it was. */
const MODAL = '[aria-modal="true"], dialog[open], [role="alertdialog"]';

/**
 * Ctrl+Z, Ctrl+Y and Ctrl+Shift+Z while a window is open (the F1 sheet, Search documents, Your
 * corrections). Chromium's own undo and redo belong to the page, not to a box: under the F1 sheet
 * Ctrl+Z took the typing back out of the Vendor box behind it and gave that box the keyboard, so
 * the next key typed into it (chunk 37a review, D16). In the window they stay a text box's own
 * while that box has typing of its own to take back, or, for redo, typing it has just taken back.
 * A list to choose from or a button has none. A menu opened by mouse leaves the keyboard in a
 * table cell, whose own undo goes on working. Every window registers it on its own window in the
 * capture phase, ahead of any handler that stops the key (the job picker, the line-items menu).
 */
export function blockUndoBehindWindow(event: KeyboardEvent) {
  if (!(event.ctrlKey || event.metaKey) || event.altKey) return;
  const letter = event.key.toLocaleLowerCase();
  const z = letter === 'z' || event.code === 'KeyZ';
  const redo =
    (z && event.shiftKey) || (!event.shiftKey && (letter === 'y' || event.code === 'KeyY'));
  if (!z && !redo) return;
  if (!document.querySelector(MODAL)) return;
  const box = textBox(event.target);
  if (box?.closest(MODAL) && (!textBoxUntouched(box) || (redo && undoneSinceFocus.has(box)))) {
    if (!redo) undoneSinceFocus.add(box);
    return;
  }
  event.preventDefault();
}

/**
 * Records what a box holds now as its untouched text. For a box whose focus handler swaps what it
 * shows (a money or number box drops its grouping for the raw value): the listener above runs
 * first and sees the resting text, so the handler calls this after the swap, or Ctrl+Z in a box
 * the person only tabbed into would count as typing and do nothing.
 */
export function noteTextBoxBaseline(target: EventTarget | null) {
  const box = textBox(target);
  if (box) heldAtFocus.set(box, box.value);
}

/** True when the text box holds exactly what it held when it took the keyboard: nothing of its own to undo. */
export function textBoxUntouched(target: EventTarget | null): boolean {
  const box = textBox(target);
  return box !== null && heldAtFocus.get(box) === box.value;
}
